<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { addDays, differenceInCalendarDays } from 'date-fns'
import { de } from 'date-fns/locale'
import { toZonedTime, format } from 'date-fns-tz'

import { useEpgAirTimeHelper } from '../../../../../composables/use-epg-air-time-helper'
import { usePageStore } from '../../../../../stores/page'
import Icon from '../../../../../components/shared/Icon.vue'

type Channel = { name: string, url: string }

const props = defineProps<{
  selectedAirTime?: string,
  initialAirTime?: string,
  channels?: Channel[],
  isPreview?: boolean
}>()

const emit = defineEmits<{
  'update:air-time-filter': [value: string]
}>()

const { timeZone, airTimeNow } = useEpgAirTimeHelper()
const pageStore = usePageStore()

const dayOffset = computed(() => {
  const baseAirTime = props.initialAirTime || airTimeNow.value
  const initial = toZonedTime(new Date(baseAirTime), timeZone)
  const target = toZonedTime(new Date(props.selectedAirTime || baseAirTime), timeZone)
  return differenceInCalendarDays(target, initial)
})

const formattedAirDate = computed(() => {
  if (!props.selectedAirTime) return ''
  return format(toZonedTime(new Date(props.selectedAirTime), timeZone), 'EEEE, d. MMMM yyyy', { locale: de })
})

// short form avoids line-wraps on small mobile and narrow desktop widths, see `.bx-epg-channel-header__date` CSS
const formattedAirDateShort = computed(() => {
  if (!props.selectedAirTime) return ''
  return format(toZonedTime(new Date(props.selectedAirTime), timeZone), 'EEE, dd.MM.yyyy', { locale: de })
})

// channels only carry name/url (no id) - matching against the normalized current page url is precise enough
// since channel detail urls are unique, and avoids widening the composer-schema just for this
function normalizeUrl (url: string) {
  return url.replace(/^https?:\/\/[^/]+/, '').split('?')[0].replace(/\/$/, '')
}

const currentUrl = computed(() => normalizeUrl(pageStore.pageData?.pageMeta?.url || ''))

const activeChannelUrl = computed(() => {
  return props.channels?.find(channel => normalizeUrl(channel.url) === currentUrl.value)?.url ?? ''
})

function shiftAirTime (direction: 'prev' | 'next') {
  const date = new Date(props.selectedAirTime || airTimeNow.value)
  const shifted = addDays(date, direction === 'next' ? 1 : -1)
  emit('update:air-time-filter', shifted.toISOString())
}

function onChannelChange (event: Event) {
  const url = (event.target as HTMLSelectElement).value
  if (url) window.location.href = url
}

// `appearance: base-select` (Chromium-only as of writing) lets us style the select's own popup instead of
// faking a custom dropdown - feature-detected so unsupported browsers keep today's plain native select untouched.
// Starts false and only flips post-mount (never during SSR/hydration) to avoid a hydration mismatch, since
// `CSS.supports` isn't available server-side and would otherwise disagree with the client's first render
const supportsCustomizableSelect = ref(false)

// tried closing the popup on scroll (it can't be kept below the sticky header, see _search.scss), but there's
// currently no working JS API for it: `select.blur()` does not dismiss this popup (verified), and the browser
// already blocks user-driven scroll (wheel/touch) while it's open anyway, so the risk is low in practice
onMounted(() => {
  supportsCustomizableSelect.value = !!CSS.supports?.('appearance', 'base-select')
})
</script>

<template>
  <section class="bx-search__header bx-typo--search-header bx-filters bx-filters--channel">
    <div v-if="channels?.length"
         class="bx-epg-channel-select">
      <select :value="activeChannelUrl"
              name="channel-select"
              title="Sender wechseln"
              aria-label="Sender wechseln"
              :disabled="isPreview"
              :class="{ 'bx-epg-channel-select__native--enhanced': supportsCustomizableSelect }"
              @change="onChannelChange">
        <button v-if="supportsCustomizableSelect"
                type="button">
          <selectedcontent />
          <Icon name="arrow_toggle" />
        </button>
        <option v-for="channel in channels"
                :key="channel.url"
                :value="channel.url">
          {{ channel.name }}
        </option>
      </select>
      <Icon v-if="!supportsCustomizableSelect"
            name="arrow_toggle" />
    </div>
    <div class="bx-air-date-navi">
      <button type="button"
              aria-label="Vorheriger Tag"
              :disabled="dayOffset <= -4"
              @click="shiftAirTime('prev')">
        <Icon name="arrow_pager-prev" />
      </button>
      <h2 class="bx-typo--16px">
        <span class="bx-air-date-navi__date-long">{{ formattedAirDate }}</span>
        <span class="bx-air-date-navi__date-short">{{ formattedAirDateShort }}</span>
      </h2>
      <button type="button"
              aria-label="Nächster Tag"
              :disabled="dayOffset >= 7"
              @click="shiftAirTime('next')">
        <Icon name="arrow_pager-next" />
      </button>
    </div>
  </section>
</template>
